// Lucka 8: Avbrottsplan för el, gas, vatten och media på projektnivå.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DocumentHeader from "./DocumentHeader";
import { PlugZap, Plus, Trash2 } from "lucide-react";

interface Props {
  projectId: string;
}

interface Plan {
  id: string;
  utility: string;
  area: string | null;
  affected_operations: string | null;
  planned_start: string | null;
  planned_end: string | null;
  backup_measures: string | null;
  notified_parties: string | null;
  responsible_name: string | null;
  status: string;
  notes: string | null;
}

const UTILITIES = ["El", "Gas", "Vatten", "Ventilation", "Fjärrvärme", "Sprinkler", "Data och tele", "Medicinska gaser"];
const STATUSES = [
  { value: "planned", label: "Planerad" },
  { value: "approved", label: "Godkänd av verksamheten" },
  { value: "executed", label: "Genomförd" },
  { value: "cancelled", label: "Inställd" },
];

const toLocalInput = (v: string | null) => (v ? new Date(v).toISOString().slice(0, 16) : "");
const fmt = (v: string | null) => (v ? new Date(v).toLocaleString("sv-SE") : "–");

const UtilityOutagePlans: React.FC<Props> = ({ projectId }) => {
  const [rows, setRows] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("utility_outage_plans")
      .select(
        "id, utility, area, affected_operations, planned_start, planned_end, backup_measures, notified_parties, responsible_name, status, notes"
      )
      .eq("project_id", projectId)
      .order("planned_start", { ascending: true });
    if (error) toast.error("Kunde inte läsa avbrottsplanerna");
    setRows((data as Plan[] | null) ?? []);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase.from("utility_outage_plans").insert({
      project_id: projectId,
      utility: "El",
      created_by: auth?.user?.id ?? null,
    });
    if (error) return toast.error("Kunde inte skapa avbrottsplanen");
    load();
  };

  const save = async (id: string, patch: Partial<Plan>) => {
    const { error } = await supabase.from("utility_outage_plans").update(patch as never).eq("id", id);
    if (error) return toast.error("Kunde inte spara");
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("utility_outage_plans").delete().eq("id", id);
    if (error) return toast.error("Kunde inte ta bort");
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  return (
    <div className="space-y-6">
      <DocumentHeader
        docNumber="09b"
        title="Avbrottsplan – el, gas och media"
        description="Planerade avbrott med tider, berörd verksamhet, reservåtgärder och vilka som är underrättade. Krävs i sjukhus, industri och andra avbrottskänsliga miljöer."
        icon={PlugZap}
        actions={
          <Button onClick={add}>
            <Plus className="mr-2 h-4 w-4" /> Nytt avbrott
          </Button>
        }
      />

      {loading ? (
        <p className="text-sm text-muted-foreground">Laddar…</p>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Inga avbrott planerade. Lägg till ett när el, gas eller media måste stängas av.
          </CardContent>
        </Card>
      ) : (
        rows.map((p) => (
          <Card key={p.id}>
            <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
              <div className="space-y-1">
                <CardTitle className="text-base">
                  {p.utility}
                  {p.area ? ` – ${p.area}` : ""}
                </CardTitle>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={p.status === "executed" ? "outline" : p.status === "approved" ? "default" : "secondary"}>
                    {STATUSES.find((s) => s.value === p.status)?.label ?? p.status}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {fmt(p.planned_start)} – {fmt(p.planned_end)}
                  </span>
                </div>
              </div>
              <Button variant="ghost" size="icon" aria-label="Ta bort avbrottsplan" onClick={() => remove(p.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1">
                  <Label>Media</Label>
                  <Select value={p.utility} onValueChange={(v) => save(p.id, { utility: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {UTILITIES.map((u) => (
                        <SelectItem key={u} value={u}>
                          {u}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Område</Label>
                  <Input defaultValue={p.area ?? ""} onBlur={(e) => save(p.id, { area: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label>Planerad start</Label>
                  <Input
                    type="datetime-local"
                    defaultValue={toLocalInput(p.planned_start)}
                    onBlur={(e) =>
                      save(p.id, {
                        planned_start: e.target.value ? new Date(e.target.value).toISOString() : null,
                      })
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label>Planerat slut</Label>
                  <Input
                    type="datetime-local"
                    defaultValue={toLocalInput(p.planned_end)}
                    onBlur={(e) =>
                      save(p.id, {
                        planned_end: e.target.value ? new Date(e.target.value).toISOString() : null,
                      })
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label>Ansvarig</Label>
                  <Input
                    defaultValue={p.responsible_name ?? ""}
                    onBlur={(e) => save(p.id, { responsible_name: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Status</Label>
                  <Select value={p.status} onValueChange={(v) => save(p.id, { status: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1">
                <Label>Berörd verksamhet</Label>
                <Textarea
                  defaultValue={p.affected_operations ?? ""}
                  onBlur={(e) => save(p.id, { affected_operations: e.target.value })}
                  placeholder="Vilka avdelningar, boende eller produktionslinjer påverkas?"
                />
              </div>
              <div className="space-y-1">
                <Label>Reservåtgärder</Label>
                <Textarea
                  defaultValue={p.backup_measures ?? ""}
                  onBlur={(e) => save(p.id, { backup_measures: e.target.value })}
                  placeholder="Reservkraft, tillfällig matning, flytt av verksamhet…"
                />
              </div>
              <div className="space-y-1">
                <Label>Underrättade parter</Label>
                <Textarea
                  defaultValue={p.notified_parties ?? ""}
                  onBlur={(e) => save(p.id, { notified_parties: e.target.value })}
                  placeholder="Verksamhetschef, driftcentral, larmoperatör, boende…"
                />
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default UtilityOutagePlans;
